import { baseApi } from 'services/api';
import { API_BASE_URL, API_SOURCE, API_TAG_TYPES, API_VERSION, LocalStorageKeysEnum } from 'constants/common';
import {
  ChargeCustomerCardErrorResponse,
  ChargeCustomerCardErrorStatusResponse,
  ChargeCustomerCardPayload,
  ChargeCustomerCardResponse,
  ChargeCustomerCardSuccessResponse,
  CustomerCareDetails,
  GetCommissionBaseResponse,
  GetEntityFiltersResponse,
  GetMerchantMessagesResponse,
  GetMerchantTeamsResponse,
  GetTeamBannersResponse,
  MerchantTeam,
  PaymentSystemConfigResponse,
  SubscriptionActivityPayload,
  SubscriptionActivityResponse,
  TeamBankAccountDetails,
  UserDetails
} from './app.model';
import {
  initialBannerState,
  initialTeamBankDetails,
  setCommissionBases,
  setEntityFilters,
  setMerchantMessages,
  setMerchantTeams,
  setPaymentSystemConfig,
  setTeamBankDetails,
  setUserDetails,
  updateBannersData
} from './appSlice';
import {
  loggedOut,
  setCurrentSignInMethod,
  setDeeplinkRedirectUrl,
  setHasPendingEmailSteps,
  setIsAuthenticated,
  setIsPasswordExpired
} from 'containers/auth/authSlice';
import { isDevelopment } from 'utils/envUtils';
import { clevertapCreateProfile } from 'utils/analytics';
import { signInMethods } from 'containers/auth/constants';
import { SignInMethod } from 'containers/auth/types';
import { LocalStorage } from 'services/storage';
import { TeamIdPayload } from 'containers/home/home.model';
import { store } from 'store/store';
import { FirebaseStorage } from 'services/firebase';
import { FireBaseCollections } from 'utils/firebase';

export interface UserDetailsPayload {
  isInitCall?: boolean;
  onSignInSuccess?: boolean;
  onSignUpSuccess?: boolean;
}

const appBaseAPi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getMerchantTeams: builder.query<MerchantTeam[], void>({
      query: () => ({
        url: `${API_BASE_URL.TEAM_MANAGER}/${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/merchants/teams`,
        method: 'GET'
      }),
      transformResponse: (response: GetMerchantTeamsResponse) => response.merchantTeams,
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setMerchantTeams(data));
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('getMerchantTeams Failed');
        }
      },
      extraOptions: {
        failure: 'getMerchantTeams failed'
      }
    }),

    getTeamHelp: builder.query<CustomerCareDetails, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/help`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'getTeamHelp failed',
        showToast: false
      }
    }),

    // IMPORTANT NOTE: Do not use this api post login as it will
    // respond with 401 error once the user's device token expires
    getUserDetails: builder.query<UserDetails, UserDetailsPayload>({
      query: () => ({
        url: `${API_VERSION.V1}/merchants/user-data`,
        method: 'GET',
        credentials: 'include'
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setUserDetails(data));

            if (payload?.isInitCall) {
              if (!LocalStorage.get(LocalStorageKeysEnum.CURRENT_SIGN_IN_METHOD)) {
                const phoneSignInMethod: SignInMethod = { ...signInMethods.phoneNumber, value: data?.phone };
                dispatch(setCurrentSignInMethod(phoneSignInMethod));
              }

              if (!data?.isPasswordActive) {
                dispatch(setIsPasswordExpired(true));
              }
              if (data?.isEmailVerified) {
                dispatch(setHasPendingEmailSteps(false));
              } else if (
                (!data?.isEmailVerified && data?.showVerificationPrompt) ||
                (!data.isEmailAdded && data.showEmailPrompt)
              ) {
                dispatch(setHasPendingEmailSteps(true));
              }
            }

            if (payload?.onSignInSuccess || payload?.onSignUpSuccess) {
              clevertapCreateProfile({
                Site: {
                  Phone: data?.phone
                }
              });
              dispatch(setIsAuthenticated({ isAuthenticated: true, writeToLocalStorage: true }));
            }

            // New merchants don't need the discount calculation coachmarks since the
            // feature is the default they're already using; pre-seed all 3 dismiss flags.
            if (payload?.onSignUpSuccess && data?.id) {
              const merchantId = data.id;
              const merchantFields = FireBaseCollections.Merchants.fields;
              await Promise.all([
                FirebaseStorage.updateMerchantCollectionFieldValue({
                  merchantId,
                  collectionFieldName: merchantFields.seenInvoiceDiscountCalculationCoachmarkSettingsWeb,
                  value: true
                }),
                FirebaseStorage.updateMerchantCollectionFieldValue({
                  merchantId,
                  collectionFieldName: merchantFields.seenInvoiceDiscountCalculationCoachmarkCreateInvoiceWeb,
                  value: true
                }),
                FirebaseStorage.updateMerchantCollectionFieldValue({
                  merchantId,
                  collectionFieldName: merchantFields.seenInvoiceDiscountCalculationCoachmarkAddDiscountModalWeb,
                  value: true
                })
              ]);
            }
            // Handle redirect after successful authentication
            if ((data.isDeviceAuthenticated || data.skippedAuthentication) && data.isPasswordActive) {
              const redirectUrl = store?.getState()?.rootReducer?.auth?.deeplinkRedirectUrl || '';
              if (redirectUrl) {
                // Use setTimeout to ensure the redirect happens after the current call stack completes
                setTimeout(() => {
                  window.location.href = redirectUrl;
                }, 100);
                dispatch(setDeeplinkRedirectUrl(null));
              }
            }
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.log(err);
          }

          // cookie isn't present in request header
          if (err?.meta?.response?.type === 'cors') dispatch(loggedOut());
        }
      },
      extraOptions: {
        failure: 'Failed to fetch user data'
      }
    }),
    getPaymentSystemConfig: builder.query<PaymentSystemConfigResponse, number>({
      query: teamId => {
        const url = `${API_BASE_URL.TEAM_MANAGER}/${API_VERSION.V1}/teams/${teamId}/payment-system/config`;
        return {
          url: url,
          method: 'GET'
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setPaymentSystemConfig(data));
          }
        } catch (err) {
          //
        }
      },
      extraOptions: {
        failure: 'Failed to get payment config',
        showToast: true
      }
    }),
    getTeamBankAccount: builder.query<TeamBankAccountDetails, number>({
      query: teamId => {
        const url = `${API_VERSION.V1}/team/${teamId}/bank-account`;
        return {
          url: url,
          method: 'GET'
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setTeamBankDetails(data));
          } else {
            dispatch(setTeamBankDetails(initialTeamBankDetails));
          }
        } catch (err) {
          dispatch(setTeamBankDetails(initialTeamBankDetails));
        }
      },
      extraOptions: {
        failure: 'Failed to get bank details',
        showToast: false
      }
    }),
    getTeamBanner: builder.query<GetTeamBannersResponse, number>({
      query: teamId => {
        const url = `${API_VERSION.V2}/team/${teamId}/banners/${API_SOURCE.WEB}`;
        return {
          url: url,
          method: 'GET'
        };
      },
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data?.data?.home_banners || data?.data?.payout_banners) {
            const { home_banners, payout_banners } = data.data;
            dispatch(
              updateBannersData({
                homeBanners: home_banners,
                payoutBanners: payout_banners
              })
            );
          } else {
            dispatch(updateBannersData(initialBannerState));
          }
        } catch (err) {
          //
        }
      },
      extraOptions: {
        failure: '',
        showToast: false
      }
    }),
    postSubscriptionActivity: builder.mutation<
      SubscriptionActivityResponse,
      { teamId: number; payload: SubscriptionActivityPayload }
    >({
      query: ({ teamId, payload }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/subscription-activity`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to post subscription activity',
        showToast: true
      }
    }),
    getCommissionBase: builder.query<GetCommissionBaseResponse, void>({
      query: () => ({
        url: `${API_VERSION.V1}/common/commission-base`,
        method: 'GET'
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCommissionBases(data));
        } catch (err) {
          //
        }
      },
      extraOptions: {
        failure: 'Failed to fetch commission base options',
        showToast: false
      }
    }),
    getMerchantMessages: builder.query<Record<string, string>, void>({
      query: () => ({
        url: `${API_VERSION.V1}/merchants/messages`,
        method: 'GET'
      }),
      transformResponse: (response: GetMerchantMessagesResponse) => response.messages ?? {},
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setMerchantMessages(data));
        } catch (err) {
          //
        }
      },
      extraOptions: {
        failure: 'Failed to fetch app messages',
        showToast: false
      }
    }),
    getEntityFilters: builder.query<GetEntityFiltersResponse, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/entity-filters`,
        method: 'GET'
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setEntityFilters(data));
          }
        } catch (err) {
          //
        }
      },
      extraOptions: {
        failure: 'Failed to fetch entity filters',
        showToast: false
      }
    }),
    chargeCustomerCard: builder.mutation<
      ChargeCustomerCardResponse,
      { teamId: number; payload: ChargeCustomerCardPayload }
    >({
      query: ({ teamId, payload }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/charge-customer-card`,
          method: 'POST',
          body: payload
        };
      },
      transformResponse: (response: ChargeCustomerCardErrorResponse | ChargeCustomerCardSuccessResponse) => {
        if (response?.['transactionId']) {
          return {
            success: true,
            data: response as ChargeCustomerCardSuccessResponse
          };
        }
        return {
          success: false,
          data: response as ChargeCustomerCardErrorResponse
        };
      },
      invalidatesTags: [
        API_TAG_TYPES.CUSTOMERS,
        API_TAG_TYPES.INVOICES_LIST,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.JOB_DETAILS
      ],
      extraOptions: {
        failure: 'Failed to charge customer card',
        showToast: false
      }
    }),
    chargeCustomerCardError: builder.query<
      ChargeCustomerCardErrorStatusResponse,
      { teamId: number; transactionId: string; errorCode: string }
    >({
      query: ({ teamId, transactionId, errorCode }) => {
        return {
          // eslint-disable-next-line max-len
          url: `${API_VERSION.V1}/teams/${teamId}/charge-customer-card/error?transactionId=${transactionId}&errorCode=${errorCode}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to get charge customer card error status',
        showToast: false
      }
    })
  })
});

export const {
  useGetMerchantTeamsQuery,
  useLazyGetMerchantTeamsQuery,
  useGetTeamHelpQuery,
  useLazyGetTeamHelpQuery,
  useGetUserDetailsQuery,
  useLazyGetUserDetailsQuery,
  useLazyGetPaymentSystemConfigQuery,
  useLazyGetTeamBankAccountQuery,
  useLazyGetTeamBannerQuery,
  usePostSubscriptionActivityMutation,
  useGetCommissionBaseQuery,
  useLazyGetCommissionBaseQuery,
  useGetMerchantMessagesQuery,
  useLazyGetMerchantMessagesQuery,
  useGetEntityFiltersQuery,
  useLazyGetEntityFiltersQuery,
  useChargeCustomerCardMutation,
  useLazyChargeCustomerCardErrorQuery
} = appBaseAPi;
